import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { fetchCartDeliveryTypes } from "./deliveryService";
import { mapDeliveryTypesResponse, optionKey, sharedDeliveryOptions } from "./deliveryMappers";

const useDeliverySelection = (carts) => {
  const cartIds = useMemo(() => (carts || []).map((cart) => cart.cartId), [carts]);
  const cartIdsKey = cartIds.join(",");

  const [optionsByCartId, setOptionsByCartId] = useState({});
  const [errorsByCartId, setErrorsByCartId] = useState({});
  const loadedRef = useRef(new Set());
  const [isLoading, setIsLoading] = useState(cartIds.length > 0);
  const [selectedKey, setSelectedKey] = useState(null);

  useEffect(() => {
    let cancelled = false;

    if (!cartIds.length) {
      setIsLoading(false);
      return undefined;
    }

    if (cartIds.some((cartId) => !loadedRef.current.has(cartId))) setIsLoading(true);

    Promise.all(
      cartIds.map((cartId) =>
        fetchCartDeliveryTypes(cartId)
          .then((data) => ({ cartId, options: mapDeliveryTypesResponse(data), error: null }))
          .catch((error) => ({ cartId, options: [], error }))
      )
    ).then((results) => {
      if (cancelled) return;

      setOptionsByCartId((prev) => {
        const next = { ...prev };
        results.forEach(({ cartId, options }) => {
          next[cartId] = options;
        });
        return next;
      });

      setErrorsByCartId((prev) => {
        const next = { ...prev };
        results.forEach(({ cartId, error }) => {
          next[cartId] = error;
        });
        return next;
      });

      results.forEach(({ cartId }) => loadedRef.current.add(cartId));
      setIsLoading(false);
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartIdsKey]);

  const sharedOptions = useMemo(
    () => sharedDeliveryOptions(cartIds.map((cartId) => optionsByCartId[cartId] || [])),
    [cartIds, optionsByCartId]
  );

  useEffect(() => {
    if (!sharedOptions.length) return;

    if (selectedKey && sharedOptions.some((option) => optionKey(option) === selectedKey)) return;

    const preferred =
      sharedOptions.find((option) => option.supportsWholeCart) || sharedOptions[0];

    setSelectedKey(optionKey(preferred));
  }, [sharedOptions, selectedKey]);

  const selectedOption = useMemo(
    () => sharedOptions.find((option) => optionKey(option) === selectedKey) || null,
    [sharedOptions, selectedKey]
  );

  const deliveryTypeIdForCart = useCallback(
    (cartId) => {
      if (!selectedOption) return null;

      const match = (optionsByCartId[cartId] || []).find(
        (option) => optionKey(option) === optionKey(selectedOption)
      );

      return match?.id ?? null;
    },
    [selectedOption, optionsByCartId]
  );

  const blockedLinesForOption = useCallback(
    (option, lines) =>
      (lines || []).filter((line) => option?.unsupportedProductIds?.includes(line.productId)),
    []
  );

  const hasAnyOptions = cartIds.some((cartId) => (optionsByCartId[cartId] || []).length > 0);
  const hasError = cartIds.some((cartId) => Boolean(errorsByCartId[cartId]));

  return {
    isLoading,
    hasError,
    hasAnyOptions,
    sharedOptions,
    selectedOption,
    selectKey: setSelectedKey,
    deliveryTypeIdForCart,
    blockedLinesForOption,
  };
};

export default useDeliverySelection;
